import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { getTopNavLinks } from '../utils/navigation';

const C = {
  bg:      '#0A0E1A',
  surface: '#111827',
  border:  '#1F2937',
  accent:  '#10B981',
  warn:    '#F59E0B',
  danger:  '#EF4444',
  text:    '#F9FAFB',
  muted:   '#6B7280',
  input:   '#1C2433',
};

const s = {
  page: { minHeight: '100vh', background: C.bg, color: C.text, fontFamily: "'DM Sans', 'Segoe UI', sans-serif" },
  topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 32px', borderBottom: `1px solid ${C.border}`, background: C.surface },
  logo: { fontSize: 20, fontWeight: 700, color: C.accent },
  nav: { display: 'flex', gap: 24 },
  navLink: (active) => ({ color: active ? C.accent : C.muted, textDecoration: 'none', fontSize: 14, fontWeight: active ? 700 : 400 }),
  main: { padding: '32px', maxWidth: 1280, margin: '0 auto' },
  heading: { fontSize: 26, fontWeight: 700, marginBottom: 4, letterSpacing: '-0.5px' },
  sub: { fontSize: 14, color: C.muted, marginBottom: 28 },
  toolbar: { display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' },
  input: { background: C.input, border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 14px', color: C.text, fontSize: 14, outline: 'none', minWidth: 180 },
  select: { background: C.input, border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 14px', color: C.text, fontSize: 14, outline: 'none', cursor: 'pointer' },
  btnPrimary: { background: C.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 16px', fontSize: 14, cursor: 'pointer' },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '12px 16px', borderBottom: `1px solid ${C.border}` },
  td: { padding: '13px 16px', fontSize: 13, borderBottom: `1px solid ${C.border}22` },
  badge: (status) => {
    const m = { captured: ['#10B98122', C.accent], pending: ['#F59E0B22', C.warn], failed: ['#EF444422', C.danger], authorized: ['#3B82F622', '#60A5FA'], refunded: ['#8B5CF622', '#A78BFA'] };
    const [bg, color] = m[status] || m.pending;
    return { display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: bg, color };
  },
  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderTop: `1px solid ${C.border}` },
  pageBtn: (active) => ({ background: active ? C.accent : 'transparent', color: active ? '#fff' : C.muted, border: `1px solid ${active ? C.accent : C.border}`, borderRadius: 6, padding: '5px 11px', fontSize: 13, cursor: 'pointer', margin: '0 2px' }),
  modal: { position: 'fixed', inset: 0, background: '#00000088', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalBox: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 32, width: '100%', maxWidth: 480, position: 'relative' },
  modalTitle: { fontSize: 18, fontWeight: 700, marginBottom: 20 },
  closeBtn: { position: 'absolute', top: 16, right: 16, background: 'transparent', border: 'none', color: C.muted, fontSize: 20, cursor: 'pointer', lineHeight: 1 },
  detailRow: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${C.border}22`, fontSize: 14 },
  detailLabel: { color: C.muted },
  detailValue: { fontWeight: 600 },
  emptyState: { textAlign: 'center', padding: '64px 0', color: C.muted },
};

const LABELS = { captured: 'Cobrado', pending: 'Pendiente', pending_review: 'En revisión', failed: 'Fallido', authorized: 'Autorizado', refunded: 'Reembolsado' };
const PROVIDERS = [
  { value: 'kushki', label: 'Kushki' },
  { value: 'stripe', label: 'Stripe' },
  { value: 'deuna', label: 'Deuna' },
  { value: 'custom', label: 'Custom' },
];

const PAYMENT_METHODS = [
  { value: 'card', label: 'Tarjeta' },
  { value: 'transfer', label: 'Transferencia' },
  { value: 'qr', label: 'QR' },
];

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [showNewPayment, setShowNewPayment] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [providersEnabled, setProvidersEnabled] = useState(['custom']);
  const [riskCases, setRiskCases] = useState([]);
  const [riskHistory, setRiskHistory] = useState([]);
  const [riskMetrics, setRiskMetrics] = useState(null);
  const [riskLoading, setRiskLoading] = useState(false);
  const [riskActionLoadingId, setRiskActionLoadingId] = useState(null);
  const [newPayment, setNewPayment] = useState({
    amount: '100.00',
    currency: 'USD',
    provider: 'custom',
    payment_method: 'card',
    customer_name: '',
    customer_email: '',
    description: '',
    token: '',
    payment_method_id: '',
    merchant_id: '',
  });
  const PER_PAGE = 10;
  const userRole = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null')?.role;
    } catch {
      return null;
    }
  })();
  const canViewRisk = ['admin', 'superadmin', 'operator', 'auditor', 'compliance_officer'].includes(userRole);
  const canManageRisk = ['admin', 'superadmin', 'operator', 'compliance_officer'].includes(userRole);
  const topNavLinks = getTopNavLinks(userRole);

  useEffect(() => {
    loadPaymentSettings();
    loadPayments();
    if (canViewRisk) {
      loadRiskCases();
      loadRiskMetrics();
      loadRiskHistory();
    }
  }, []);

  const loadPaymentSettings = async () => {
    try {
      const r = await api.get('/admin/payment-settings');
      const enabled = Array.isArray(r.data?.providers_enabled) && r.data.providers_enabled.length
        ? r.data.providers_enabled
        : ['custom'];
      const defaultProvider = r.data?.provider_default && enabled.includes(r.data.provider_default)
        ? r.data.provider_default
        : enabled[0];

      setProvidersEnabled(enabled);
      setNewPayment(prev => ({ ...prev, provider: defaultProvider }));
    } catch {
      setProvidersEnabled(['custom']);
      setNewPayment(prev => ({ ...prev, provider: 'custom' }));
    }
  };

  const loadPayments = async () => {
    setLoading(true);
    try {
      const r = await api.get('/merchants/payments?limit=100');
      setPayments(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      const message = e?.response?.data?.error || 'No se pudo cargar el listado de pagos';
      notify(`❌ ${message}`, 'error');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const loadRiskCases = async () => {
    setRiskLoading(true);
    try {
      const r = await api.get('/payments/risk-cases?status=open&limit=30');
      setRiskCases(Array.isArray(r.data) ? r.data : []);
    } catch {
      setRiskCases([]);
    } finally {
      setRiskLoading(false);
    }
  };

  const loadRiskMetrics = async () => {
    try {
      const r = await api.get('/payments/risk-cases/metrics?window=7d');
      setRiskMetrics(r.data?.summary || null);
    } catch {
      setRiskMetrics(null);
    }
  };

  const loadRiskHistory = async () => {
    try {
      const r = await api.get('/payments/risk-cases/history?limit=20');
      setRiskHistory(Array.isArray(r.data) ? r.data : []);
    } catch {
      setRiskHistory([]);
    }
  };

  const filtered = payments.filter(p => {
    const matchSearch = !search || 
      p.payment_id?.includes(search) ||
      p.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
      p.customer_email?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || p.status === filter;
    return matchSearch && matchFilter;
  });

  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.ceil(filtered.length / PER_PAGE);

  const handleCapture = async (paymentId) => {
    setActionLoading(true);
    try {
      await api.post(`/merchants/payments/${paymentId}/capture`);
      notify('✅ Pago capturado exitosamente', 'success');
      loadPayments();
    } catch (e) {
      notify('❌ Error al capturar el pago', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveRiskCase = async (caseId) => {
    const notes = window.prompt('Motivo de aprobación (obligatorio):', 'Aprobado tras revisión manual');
    if (!notes || !notes.trim()) {
      notify('❌ Debes ingresar un motivo de aprobación', 'error');
      return;
    }

    setRiskActionLoadingId(caseId);
    try {
      await api.post(`/payments/risk-cases/${caseId}/approve`, { notes: notes.trim() });
      notify('✅ Caso de riesgo aprobado', 'success');
      await loadRiskCases();
      await loadRiskMetrics();
      await loadRiskHistory();
      await loadPayments();
    } catch (e) {
      const message = e?.response?.data?.error || 'No se pudo aprobar el caso';
      notify(`❌ ${message}`, 'error');
    } finally {
      setRiskActionLoadingId(null);
    }
  };

  const handleRejectRiskCase = async (caseId) => {
    const notes = window.prompt('Motivo de rechazo (obligatorio):', 'Rechazado por análisis manual de riesgo');
    if (!notes || !notes.trim()) {
      notify('❌ Debes ingresar un motivo de rechazo', 'error');
      return;
    }

    setRiskActionLoadingId(caseId);
    try {
      await api.post(`/payments/risk-cases/${caseId}/reject`, { notes: notes.trim() });
      notify('✅ Caso de riesgo rechazado', 'success');
      await loadRiskCases();
      await loadRiskMetrics();
      await loadRiskHistory();
      await loadPayments();
    } catch (e) {
      const message = e?.response?.data?.error || 'No se pudo rechazar el caso';
      notify(`❌ ${message}`, 'error');
    } finally {
      setRiskActionLoadingId(null);
    }
  };

  const notify = (text, type) => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3500);
  };

  const fmt = (v) => `$${parseFloat(v || 0).toLocaleString('es-EC', { minimumFractionDigits: 2 })}`;
  const fmtDate = (iso) => new Date(iso).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const updateNewPayment = (key, value) => {
    setNewPayment(prev => ({ ...prev, [key]: value }));
  };

  const resetNewPayment = () => {
    setNewPayment(prev => ({
      ...prev,
      amount: '100.00',
      currency: 'USD',
      payment_method: 'card',
      customer_name: '',
      customer_email: '',
      description: '',
      token: '',
      payment_method_id: '',
      merchant_id: '',
    }));
  };

  const closeNewPayment = () => {
    setShowNewPayment(false);
    resetNewPayment();
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    if (!newPayment.amount || Number(newPayment.amount) <= 0) {
      notify('❌ El monto debe ser mayor a 0', 'error');
      return;
    }

    if (newPayment.provider === 'stripe' && !newPayment.payment_method_id.trim()) {
      notify('❌ Stripe requiere payment_method_id', 'error');
      return;
    }

    if (newPayment.provider !== 'stripe' && !newPayment.token.trim()) {
      notify('❌ Debes ingresar token del proveedor', 'error');
      return;
    }

    const payload = {
      amount: Number(newPayment.amount),
      currency: (newPayment.currency || 'USD').toUpperCase(),
      provider: newPayment.provider,
      payment_method: newPayment.payment_method,
      description: newPayment.description || undefined,
      customer: {
        name: newPayment.customer_name || undefined,
        email: newPayment.customer_email || undefined,
      },
      metadata: {},
    };

    if (newPayment.provider === 'stripe') {
      payload.payment_method_id = newPayment.payment_method_id.trim();
    } else {
      payload.token = newPayment.token.trim();
    }

    if (newPayment.merchant_id.trim()) {
      payload.merchant_id = newPayment.merchant_id.trim();
    }

    setActionLoading(true);
    try {
      const res = await api.post('/merchants/payments', payload);
      if (res.status === 202) {
        notify('⚠️ Pago enviado a revisión manual de riesgo', 'success');
      } else {
        notify('✅ Pago creado exitosamente', 'success');
      }
      closeNewPayment();
      await loadPayments();
      if (canViewRisk) {
        await loadRiskCases();
        await loadRiskMetrics();
      }
    } catch (e2) {
      const message = e2?.response?.data?.error || 'Error al crear el pago';
      notify(`❌ ${message}`, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div style={s.page}>
      {/* Topbar */}
      <div style={s.topbar}>
        <div style={s.logo}>⚡ FintechCoop</div>
        <nav style={s.nav}>
          {topNavLinks.map(link => (
            <Link key={link.to} to={link.to} style={s.navLink(link.to === '/payments')}>
              {link.label}
            </Link>
          ))}
        </nav>
      </div>

      {/* Toast */}
      {msg && (
        <div style={{ position: 'fixed', top: 20, right: 20, background: msg.type === 'success' ? C.accent : C.danger, color: '#fff', padding: '12px 20px', borderRadius: 10, fontSize: 14, fontWeight: 600, zIndex: 2000, boxShadow: '0 4px 20px #0008' }}>
          {msg.text}
        </div>
      )}

      <div style={s.main}>
        <div style={s.heading}>Pagos</div>
        <div style={s.sub}>Gestiona y monitorea todas las transacciones de tu comercio</div>

        {/* Toolbar */}
        <div style={s.toolbar}>
          <input
            style={s.input}
            placeholder="Buscar por ID, cliente, email..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          <select style={s.select} value={filter} onChange={e => { setFilter(e.target.value); setPage(1); }}>
            <option value="all">Todos los estados</option>
            <option value="captured">Cobrados</option>
            <option value="pending">Pendientes</option>
            <option value="pending_review">En revisión</option>
            <option value="authorized">Autorizados</option>
            <option value="failed">Fallidos</option>
            <option value="refunded">Reembolsados</option>
          </select>
          <div style={{ marginLeft: 'auto' }}>
            <button style={s.btnPrimary} onClick={() => setShowNewPayment(true)}>
              + Nuevo pago
            </button>
          </div>
        </div>

        {/* Tabla */}
        <div style={s.card}>
          {loading ? (
            <div style={s.emptyState}><div style={{ fontSize: 28, marginBottom: 8 }}>⏳</div>Cargando...</div>
          ) : paginated.length === 0 ? (
            <div style={s.emptyState}><div style={{ fontSize: 36, marginBottom: 8 }}>📭</div>No se encontraron pagos</div>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  {['ID Pago', 'Cliente', 'Monto', 'Método', 'Fecha', 'Estado', 'Acción'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map(p => (
                  <tr key={p.payment_id} style={{ cursor: 'pointer' }} onClick={() => setSelected(p)}>
                    <td style={{ ...s.td, fontFamily: 'monospace', color: C.muted, fontSize: 12 }}>
                      {p.payment_id?.slice(-10).toUpperCase()}
                    </td>
                    <td style={s.td}>
                      <div style={{ fontWeight: 600 }}>{p.customer_name || '—'}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{p.customer_email || ''}</div>
                    </td>
                    <td style={{ ...s.td, fontWeight: 700, fontSize: 15 }}>{fmt(p.amount)}</td>
                    <td style={{ ...s.td, color: C.muted }}>
                      {p.payment_method === 'card' ? '💳 Tarjeta' : p.payment_method === 'qr' ? '📲 QR' : '🏦 Transf.'}
                    </td>
                    <td style={{ ...s.td, color: C.muted, fontSize: 12 }}>{fmtDate(p.created_at)}</td>
                    <td style={s.td}><span style={s.badge(p.status)}>{LABELS[p.status] || p.status}</span></td>
                    <td style={s.td} onClick={e => e.stopPropagation()}>
                      {p.status === 'authorized' && (
                        <button style={{ ...s.btnPrimary, padding: '5px 12px', fontSize: 12 }}
                          disabled={actionLoading}
                          onClick={() => handleCapture(p.payment_id)}>
                          Capturar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Paginación */}
          {totalPages > 1 && (
            <div style={s.pagination}>
              <span style={{ fontSize: 13, color: C.muted }}>{filtered.length} pagos · Página {page} de {totalPages}</span>
              <div>
                <button style={s.pageBtn(false)} disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map(n => (
                  <button key={n} style={s.pageBtn(n === page)} onClick={() => setPage(n)}>{n}</button>
                ))}
                <button style={s.pageBtn(false)} disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
              </div>
            </div>
          )}
        </div>

        {canViewRisk && (
          <div style={{ ...s.card, marginTop: 20 }}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>Cola de revisión de riesgo</div>
              <button
                style={s.btnSecondary}
                onClick={async () => {
                  await loadRiskCases();
                  await loadRiskMetrics();
                  await loadRiskHistory();
                }}
                disabled={riskLoading}
              >
                Actualizar
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))', gap: 12, padding: '14px 20px', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ background: C.input, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
                <div style={{ fontSize: 11, color: C.muted }}>Casos 7d</div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{riskMetrics?.total_cases ?? 0}</div>
              </div>
              <div style={{ background: C.input, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
                <div style={{ fontSize: 11, color: C.muted }}>Aprobación</div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{riskMetrics?.approval_rate ?? 0}%</div>
              </div>
              <div style={{ background: C.input, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
                <div style={{ fontSize: 11, color: C.muted }}>Rechazo</div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{riskMetrics?.rejection_rate ?? 0}%</div>
              </div>
              <div style={{ background: C.input, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
                <div style={{ fontSize: 11, color: C.muted }}>Score promedio</div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{riskMetrics?.avg_risk_score ?? '—'}</div>
              </div>
            </div>
            {riskLoading ? (
              <div style={{ ...s.emptyState, padding: '28px 0' }}>Cargando casos...</div>
            ) : riskCases.length === 0 ? (
              <div style={{ ...s.emptyState, padding: '28px 0' }}>No hay casos pendientes</div>
            ) : (
              <table style={s.table}>
                <thead>
                  <tr>
                    {['Caso', 'Pago', 'Cliente', 'Monto', 'Score', 'Señales', ...(canManageRisk ? ['Acción'] : [])].map(h => (
                      <th key={h} style={s.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {riskCases.map(rc => (
                    <tr key={rc.id}>
                      <td style={s.td}>#{rc.id}</td>
                      <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 12 }}>{rc.payment?.payment_id?.slice(-10)?.toUpperCase() || '—'}</td>
                      <td style={s.td}>{rc.payment?.customer_email || rc.payment?.customer_name || '—'}</td>
                      <td style={s.td}>{fmt(rc.payment?.amount)}</td>
                      <td style={s.td}>{rc.risk_score}</td>
                      <td style={{ ...s.td, color: C.muted }}>{(rc.risk_signals || []).join(', ') || '—'}</td>
                      {canManageRisk && (
                        <td style={s.td}>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              style={{ ...s.btnPrimary, padding: '5px 12px', fontSize: 12 }}
                              disabled={riskActionLoadingId === rc.id}
                              onClick={() => handleApproveRiskCase(rc.id)}
                            >
                              Aprobar
                            </button>
                            <button
                              style={{ ...s.btnSecondary, padding: '5px 12px', fontSize: 12, color: C.danger, borderColor: `${C.danger}66` }}
                              disabled={riskActionLoadingId === rc.id}
                              onClick={() => handleRejectRiskCase(rc.id)}
                            >
                              Rechazar
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div style={{ padding: '14px 20px', borderTop: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Historial reciente de decisiones</div>
              {riskHistory.length === 0 ? (
                <div style={{ fontSize: 12, color: C.muted }}>Sin decisiones recientes</div>
              ) : (
                <table style={s.table}>
                  <thead>
                    <tr>
                      {['Caso', 'Acción', 'Notas', 'Usuario', 'Fecha'].map(h => (
                        <th key={h} style={s.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {riskHistory.slice(0, 8).map(d => (
                      <tr key={d.id}>
                        <td style={s.td}>#{d.case_id}</td>
                        <td style={s.td}>{d.action === 'approved' ? 'Aprobado' : 'Rechazado'}</td>
                        <td style={{ ...s.td, color: C.muted }}>{d.notes}</td>
                        <td style={s.td}>{d.decided_by_user_id}</td>
                        <td style={s.td}>{d.decided_at ? fmtDate(d.decided_at) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal: detalle de pago */}
      {selected && (
        <div style={s.modal} onClick={() => setSelected(null)}>
          <div style={s.modalBox} onClick={e => e.stopPropagation()}>
            <button style={s.closeBtn} onClick={() => setSelected(null)}>×</button>
            <div style={s.modalTitle}>Detalle del pago</div>
            {[
              ['ID',        selected.payment_id],
              ['Cliente',   selected.customer_name],
              ['Email',     selected.customer_email],
              ['Monto',     fmt(selected.amount)],
              ['Moneda',    selected.currency || 'USD'],
              ['Método',    selected.payment_method === 'card' ? 'Tarjeta' : selected.payment_method === 'qr' ? 'QR' : 'Transferencia'],
              ['Proveedor', selected.provider || '—'],
              ['Tarjeta',   selected.card_last4 ? `•••• ${selected.card_last4}` : '—'],
              ['Estado',    LABELS[selected.status] || selected.status],
              ['Creado',    fmtDate(selected.created_at)],
              ['Capturado', selected.captured_at ? fmtDate(selected.captured_at) : '—'],
            ].map(([label, value]) => (
              <div key={label} style={s.detailRow}>
                <span style={s.detailLabel}>{label}</span>
                <span style={s.detailValue}>{value || '—'}</span>
              </div>
            ))}
            {selected.status === 'authorized' && (
              <button style={{ ...s.btnPrimary, width: '100%', marginTop: 20, padding: 12 }}
                disabled={actionLoading}
                onClick={() => { handleCapture(selected.payment_id); setSelected(null); }}>
                {actionLoading ? 'Capturando...' : 'Capturar pago'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Modal: nuevo pago multigateway */}
      {showNewPayment && (
        <div style={s.modal} onClick={closeNewPayment}>
          <div style={s.modalBox} onClick={e => e.stopPropagation()}>
            <button style={s.closeBtn} onClick={closeNewPayment}>×</button>
            <div style={s.modalTitle}>Nuevo pago</div>

            <form onSubmit={handleCreatePayment}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                <input
                  style={s.input}
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="Monto"
                  value={newPayment.amount}
                  onChange={e => updateNewPayment('amount', e.target.value)}
                  required
                />
                <input
                  style={s.input}
                  placeholder="Moneda (USD)"
                  value={newPayment.currency}
                  maxLength={3}
                  onChange={e => updateNewPayment('currency', e.target.value.toUpperCase())}
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                <select
                  style={s.select}
                  value={newPayment.provider}
                  onChange={e => updateNewPayment('provider', e.target.value)}
                >
                  {PROVIDERS.filter(p => providersEnabled.includes(p.value)).map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
                <select
                  style={s.select}
                  value={newPayment.payment_method}
                  onChange={e => updateNewPayment('payment_method', e.target.value)}
                >
                  {PAYMENT_METHODS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              <input
                style={{ ...s.input, width: '100%', marginBottom: 10 }}
                placeholder="Nombre cliente"
                value={newPayment.customer_name}
                onChange={e => updateNewPayment('customer_name', e.target.value)}
              />
              <input
                style={{ ...s.input, width: '100%', marginBottom: 10 }}
                type="email"
                placeholder="Email cliente"
                value={newPayment.customer_email}
                onChange={e => updateNewPayment('customer_email', e.target.value)}
              />
              <input
                style={{ ...s.input, width: '100%', marginBottom: 10 }}
                placeholder="Descripción"
                value={newPayment.description}
                onChange={e => updateNewPayment('description', e.target.value)}
              />
              <input
                style={{ ...s.input, width: '100%', marginBottom: 10 }}
                placeholder="merchant_id (opcional)"
                value={newPayment.merchant_id}
                onChange={e => updateNewPayment('merchant_id', e.target.value)}
              />

              {newPayment.provider === 'stripe' ? (
                <input
                  style={{ ...s.input, width: '100%', marginBottom: 10 }}
                  placeholder="payment_method_id (Stripe)"
                  value={newPayment.payment_method_id}
                  onChange={e => updateNewPayment('payment_method_id', e.target.value)}
                  required
                />
              ) : (
                <input
                  style={{ ...s.input, width: '100%', marginBottom: 10 }}
                  placeholder={`token (${newPayment.provider})`}
                  value={newPayment.token}
                  onChange={e => updateNewPayment('token', e.target.value)}
                  required
                />
              )}

              <button
                style={{ ...s.btnPrimary, width: '100%', marginTop: 6, padding: 12 }}
                type="submit"
                disabled={actionLoading}
              >
                {actionLoading ? 'Procesando...' : 'Crear pago'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
